import React, { useState } from "react";
import { Divider, Box, Stack, MenuItem } from "@mui/material";
import { PreviewContainer, PreviewItem } from "../../../../components";
import { formatWithoutTimezone } from "../../../../utils/common";
import { useDispatch, useSelector } from "react-redux";
import { getCountriesData } from "../../../../redux/actions/config";
import { useEffect } from "react";
import { TextFieldSelect, TextFieldComponent } from "../../../../components";
import { useFormik } from "formik";
import * as yup from "yup";
import { ButtonPrimary } from "../../../../components";
import { toast } from "react-toastify";

const BusinessDetailsTab = ({ kycData, onStatusUpdate }) => {
  const { countries } = useSelector((store) => store?.configReducer);
  const dispatch = useDispatch();
  const [statusUpdateLoading, setStatusUpdateLoading] = useState(false);

  useEffect(() => {
    dispatch(getCountriesData());
  }, [dispatch]);

  // Helper function to get country name from code
  const getCountryName = (countryCode) => {
    if (!countryCode) return "--";
    const country = countries?.find((c) => c.code === countryCode || c.id === countryCode);
    return country?.name || countryCode;
  };

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return "--";
    try {
      let normalizedDate = String(dateString);
      if (normalizedDate.endsWith("GMT")) {
        normalizedDate = normalizedDate.replace(/GMT$/, "Z");
      }
      return formatWithoutTimezone(normalizedDate, "dd MMM yyyy HH:mm a");
    } catch {
      return dateString;
    }
  };

  // Format establishment date (YYYY-MM-DD format)
  const formatEstablishmentDate = (dateString) => {
    if (!dateString) return "--";
    try {
      const dateObj = new Date(dateString);
      if (!isNaN(dateObj.getTime())) {
        return formatWithoutTimezone(dateString, "dd MMM yyyy");
      }
      return dateString;
    } catch {
      return dateString || "--";
    }
  };

  // Format phone number
  const formatPhoneNumber = () => {
    if (kycData?.mobile_number) {
      const countryCode = kycData.mobile_country_code || "";
      const number = kycData.mobile_number;
      return countryCode ? `+${countryCode} ${number}` : number;
    }
    return "--";
  };

  const validationSchema = yup.object({
    kyb_status: yup.string().required("KYB Status is required"),
    notes: yup.string().when("kyb_status", {
      is: "rejected",
      then: (schema) => schema.required("Notes are required for rejection"),
      otherwise: (schema) => schema,
    }),
  });

  const formik = useFormik({
    initialValues: {
      kyb_status: kycData?.kyc_review_status === "approved" ? "approved" : 
                   kycData?.kyc_review_status === "rejected" ? "rejected" : "",
      notes: "",
    },
    validationSchema: validationSchema,
    enableReinitialize: true,
  });

  const handleStatusUpdate = async () => {
    if (!formik.values.kyb_status) {
      toast.error("Please select a status");
      return;
    }

    if (formik.values.kyb_status === "rejected" && !formik.values.notes.trim()) {
      toast.error("Notes are required for rejection");
      return;
    }

    setStatusUpdateLoading(true);
    try {
      const action = formik.values.kyb_status === "approved" ? "approve" : "reject";
      await onStatusUpdate(action, formik.values.notes);
      formik.resetForm();
    } catch (error) {
      // Error handled in parent
    } finally {
      setStatusUpdateLoading(false);
    }
  };

  if (!kycData) {
    return null;
  }

  return (
    <>
      {/* Section 1: Basic Information */}
      <PreviewContainer title="Basic Information">
        <PreviewItem 
          title="KYC ID" 
          content={kycData?.id || "--"} 
        />
        <PreviewItem 
          title="Unique Key" 
          content={kycData?.unique_key || "--"} 
        />
        <PreviewItem 
          title="Legal Name" 
          content={kycData?.legal_name || "--"} 
        />
        {kycData?.business_name && (
          <PreviewItem 
            title="Business Name" 
            content={kycData.business_name} 
          />
        )}
        <PreviewItem 
          title="Registration Number" 
          content={kycData?.registration_number || "--"} 
        />
        <PreviewItem 
          title="Tax ID" 
          content={kycData?.tax_id || "--"} 
        />
        <PreviewItem
          title="Establishment Date"
          content={formatEstablishmentDate(kycData?.establishment_date)}
        />
        <PreviewItem
          title="Organization Type"
          content={
            kycData?.organizationType_obj?.name || 
            kycData?.organizationType_obj?.code || 
            kycData?.organization_type ||
            "--"
          }
        />
        <PreviewItem
          title="Line of Business"
          content={
            kycData?.lineOfBusiness_obj?.name || 
            kycData?.lineOfBusiness_obj?.code || 
            kycData?.line_of_business ||
            "--"
          }
        />
        {kycData?.industry && (
          <PreviewItem
            title="Industry"
            content={kycData.industry}
          />
        )}
        {kycData?.business_description && (
          <PreviewItem
            title="Business Description"
            content={kycData.business_description}
          />
        )}
      </PreviewContainer>

      <Divider sx={{ borderStyle: "dashed", my: 3 }} variant={"middle"} />

      {/* Section 2: Address Information */}
      {kycData?.address_line1 && (
        <>
          <PreviewContainer title="Address Information">
            <PreviewItem 
              title="Address Line 1" 
              content={kycData?.address_line1 || "--"} 
            />
            {kycData?.address_line2 && (
              <PreviewItem 
                title="Address Line 2" 
                content={kycData.address_line2} 
              />
            )}
            <PreviewItem 
              title="City" 
              content={kycData?.city_obj?.name || kycData?.city || "--"} 
            />
            <PreviewItem 
              title="Province" 
              content={kycData?.province_obj?.name || kycData?.province || "--"} 
            />
            <PreviewItem 
              title="Postal Code" 
              content={kycData?.postal_code || "--"} 
            />
            <PreviewItem 
              title="Country" 
              content={kycData?.country_obj?.name || getCountryName(kycData?.country) || "--"} 
            />
          </PreviewContainer>

          <Divider sx={{ borderStyle: "dashed", my: 3 }} variant={"middle"} />
        </>
      )}

      {/* Section 3: Contact Information */}
      {(kycData?.email_address || kycData?.mobile_number) && (
        <>
          <PreviewContainer title="Contact Information">
            {kycData?.email_address && (
              <PreviewItem 
                title="Email Address" 
                content={kycData.email_address} 
              />
            )}
            {kycData?.mobile_number && (
              <PreviewItem 
                title="Mobile Number" 
                content={formatPhoneNumber()} 
              />
            )}
            {kycData?.primary_contact_name && (
              <PreviewItem 
                title="Primary Contact Name" 
                content={kycData.primary_contact_name} 
              />
            )}
          </PreviewContainer>

          <Divider sx={{ borderStyle: "dashed", my: 3 }} variant={"middle"} />
        </>
      )}

      {/* Section 4: Location Information */}
      {(kycData?.country_of_registration || kycData?.country_of_operation) && (
        <>
          <PreviewContainer title="Location Information">
            {kycData?.country_of_registration && (
              <PreviewItem 
                title="Country of Registration" 
                content={kycData?.countryOfRegistration_obj?.name || getCountryName(kycData.country_of_registration) || "--"} 
              />
            )}
            {kycData?.country_of_operation && (
              <PreviewItem 
                title="Country of Operation" 
                content={kycData?.countryOfOperation_obj?.name || getCountryName(kycData.country_of_operation) || "--"} 
              />
            )}
          </PreviewContainer>

          <Divider sx={{ borderStyle: "dashed", my: 3 }} variant={"middle"} />
        </>
      )}

      {/* Section 5: Status Information */}
      <PreviewContainer title="Status Information">
        <PreviewItem 
          title="KYC Review Status" 
          content={kycData?.kyc_review_status?.replace(/_/g, " ") || "--"} 
        />
        <PreviewItem 
          title="Status" 
          content={kycData?.status || "--"} 
        />
        <PreviewItem 
          title="Party Type" 
          content={kycData?.party_type_display || (kycData?.party_type === "B" ? "Business" : kycData?.party_type === "I" ? "Individual" : "--")} 
        />
        {kycData?.kyc_submitted_date && (
          <PreviewItem 
            title="KYC Submitted Date" 
            content={formatDate(kycData.kyc_submitted_date)} 
          />
        )}
        {kycData?.kyc_review_date && (
          <PreviewItem 
            title="KYC Review Date" 
            content={formatDate(kycData.kyc_review_date)} 
          />
        )}
        {kycData?.kyc_review_notes && (
          <PreviewItem 
            title="KYC Review Notes" 
            content={kycData.kyc_review_notes} 
          />
        )}
      </PreviewContainer>

      <Divider sx={{ borderStyle: "dashed", my: 3 }} variant={"middle"} />

      {/* Section 6: KYB Status Update */}
      {kycData?.kyc_review_status === "pending_review" && (
        <>
          <PreviewContainer title="Update KYB Status">
            <Box sx={{ mb: 3 }}>
              <TextFieldSelect
                formik={formik}
                name="kyb_status"
                type="text"
                label="KYB Status"
                options={[
                  {
                    name: "Approved",
                    value: "approved",
                  },
                  {
                    name: "Rejected",
                    value: "rejected",
                  },
                ]}
                render={(item) => (
                  <MenuItem value={item.value}>{item.name}</MenuItem>
                )}
              />
            </Box>
            {formik.values.kyb_status === "rejected" && (
              <Box sx={{ mb: 3 }}>
                <TextFieldComponent
                  formik={formik}
                  name="notes"
                  type="text"
                  label="Rejection Notes (Required)"
                  multiline
                  rows={4}
                  placeholder="Please provide reason for rejection"
                />
              </Box>
            )}
            <Stack direction="row" gap={2} justifyContent="flex-end">
              <ButtonPrimary
                onClick={handleStatusUpdate}
                disabled={!formik.isValid || statusUpdateLoading}
              >
                {statusUpdateLoading ? "Updating..." : "Update Status"}
              </ButtonPrimary>
            </Stack>
          </PreviewContainer>
        </>
      )}
    </>
  );
};

export default BusinessDetailsTab;

